import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { ImportMappingSpec, ImportValueField } from "@/model/interface/DataImport";

interface ImportValuesStepProps {
  valueFields: ImportValueField[];
  mapping: ImportMappingSpec;
  onMappingChange: (mapping: ImportMappingSpec) => void;
}

export default function ImportValuesStep({ valueFields, mapping, onMappingChange }: ImportValuesStepProps) {
  function selectedOption(field: string, value: string, suggested: string): string {
    return mapping.values[field]?.[value] ?? suggested;
  }

  function updateValue(field: string, value: string, option: string) {
    onMappingChange({
      ...mapping,
      values: { ...mapping.values, [field]: { ...(mapping.values[field] ?? {}), [value]: option } },
    });
  }

  if (valueFields.length === 0) {
    return <p className="text-sm text-muted-foreground">Nothing to match here. Continue to the preview.</p>;
  }

  return (
    <div className="max-h-[55vh] space-y-6 overflow-auto pr-1">
      <p className="text-sm text-muted-foreground">
        Match the values in your file to the ones used here. Each row shows how many times the value appears.
      </p>
      {valueFields.map((valueField) => (
        <div key={valueField.field} className="space-y-2">
          <h3 className="text-sm font-medium">{valueField.label}</h3>
          <div className="rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Value in your file</TableHead>
                  <TableHead className="w-24 text-right">Rows</TableHead>
                  <TableHead>Import as</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {valueField.values.map((valueCount) => (
                  <TableRow key={valueCount.value}>
                    <TableCell className="font-medium">{valueCount.value}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {valueCount.count.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={selectedOption(valueField.field, valueCount.value, valueCount.suggested)}
                        onValueChange={(option) => updateValue(valueField.field, valueCount.value, option)}
                      >
                        <SelectTrigger className="h-8 w-60">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {valueField.options.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ))}
    </div>
  );
}
